/**
 * The «this number exists and is not for you» treatment.
 *
 * Every screen that could show a fund's balance used to fall back to «-» when
 * the manager had switched «نمایش موجودی صندوق» off. That is the same glyph
 * those screens print for a date they simply do not have, so members read it
 * as «the app has no number for this fund» rather than «the number is withheld
 * from me». Masked digits behind a lock say the second thing.
 *
 * Both pieces draw in `currentColor` and carry no colour of their own, so each
 * surface sets its own: `text-text-secondary` on the white cards and headers,
 * plain inherited white inside the balance dropdown. That is the only way one
 * component can sit on both without a palette argument, and it keeps the
 * tokens in `tailwind.config.js` as the single source of colour.
 */

/** The padlock, sized to the text it sits next to. */
export function LockIcon({ className = 'w-3.5 h-3.5' }: { className?: string }) {
    return (
        <svg
            className={`${className} shrink-0`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
    )
}

/**
 * A withheld figure: «🔒 ●●●●●●●» where the amount would be.
 *
 * The dots are decoration and are `aria-hidden` — read aloud they are seven
 * meaningless bullets. Whatever explains the masking in words (the caption on
 * a fund card, the sentence inside the balance dropdown) is real text and is
 * what a screen reader should reach instead.
 */
export default function HiddenAmount({
    className = '',
}: {
    className?: string
}) {
    return (
        <span className={`inline-flex items-center gap-1.5 ${className}`}>
            <LockIcon />
            <span aria-hidden="true" className="tracking-[0.15em]">
                ●●●●●●●
            </span>
        </span>
    )
}

/**
 * One sentence, used verbatim wherever the masking is explained, so a member
 * who meets it on the funds list and again inside a fund reads the same words
 * rather than two guesses at the same fact.
 */
export const HIDDEN_BALANCE_REASON =
    'مدیر نمایش موجودی را برای اعضا بسته است'
